import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { ArrowLeft, Send, AlertTriangle, Flag, X } from "lucide-react";
import api from "@/lib/api";
import { getInitials, avatarColor } from "@/lib/avatar";
import SafeTradeBanner from "@/components/shared/SafeTradeBanner";
import { isScammy } from "@/lib/scamGuard";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

export default function MarketThread({ peer, productId, onBack, socketMessage }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportTag, setReportTag] = useState("");
  const [reportReason, setReportReason] = useState("");
  const [reporting, setReporting] = useState(false);
  const endRef = useRef(null);
  const REASON_TAGS = [
    { id: "off_app", label: t("m_reason_offapp") },
    { id: "fake", label: t("m_reason_fake") },
    { id: "no_show", label: t("m_reason_noshow") },
    { id: "harassment", label: t("m_reason_harassment") },
    { id: "other", label: t("m_reason_other") },
  ];

  const load = () => api.get(`/market/inbox/${peer.id}`).then(({ data }) => setMessages(data.messages)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [peer.id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => {
    if (!socketMessage) return;
    const m = socketMessage;
    if ((m.from === peer.id && m.to === user.id) || (m.from === user.id && m.to === peer.id)) {
      setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
    }
  }, [socketMessage, peer.id, user.id]);

  const send = async () => {
    if (!text.trim()) return;
    const t = text.trim(); setText("");
    try { const { data } = await api.post(`/market/inbox/${peer.id}`, { text: t, product_id: productId }); setMessages((prev) => [...prev, data]); } catch {}
  };

  const submitReport = async () => {
    const tag = REASON_TAGS.find((r) => r.id === reportTag);
    const parts = [];
    if (tag) parts.push(tag.label);
    if (reportReason.trim()) parts.push(reportReason.trim());
    const reason = parts.join(" — ");
    try {
      setReporting(true);
      await api.post("/market/report", { reported_id: peer.id, product_id: productId || null, reason });
      setReportOpen(false); setReportReason(""); setReportTag("");
      toast.success(t("m_report_sent"));
    } catch { toast.error(t("s_failed")); } finally { setReporting(false); }
  };

  const composeRisky = isScammy(text);

  return (
    <div className="flex flex-col h-full" data-testid="market-thread">
      <div className="flex items-center gap-3 pb-3 border-b border-[var(--rc-border)]">
        <button onClick={onBack} data-testid="market-thread-back" className="text-[var(--rc-silver)]"><ArrowLeft size={22} /></button>
        <div className="h-10 w-10 rounded-full grid place-items-center text-[var(--rc-gold)] font-semibold border-2 border-[var(--rc-gold)] overflow-hidden" style={{ backgroundColor: peer.avatar ? "var(--rc-bg)" : avatarColor(peer.name) }}>
          {peer.avatar ? <img src={peer.avatar} alt="" className="h-full w-full object-cover" /> : getInitials(peer.name)}
        </div>
        <div className="font-medium truncate flex-1">{peer.name}</div>
        <button onClick={() => setReportOpen(true)} data-testid="market-report-btn" title={t("m_report_chat")}
          className="h-9 w-9 grid place-items-center rounded-full text-[var(--rc-muted)] hover:text-red-400 shrink-0"><Flag size={18} /></button>
      </div>
      <div className="flex-1 overflow-y-auto no-scrollbar py-4 space-y-2">
        <SafeTradeBanner testid="market-safe-trade" storageKey="rc_safetrade_chat_dismissed" className="mb-3" />
        {messages.map((m) => {
          const mine = m.from === user.id;
          const risky = isScammy(m.text);
          return (
            <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`} data-testid={`market-msg-${m.id}`}>
              <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 ${mine ? "bg-[var(--rc-bubble-out)]" : "bg-[var(--rc-surface)]"}`}>
                <div className="text-sm [overflow-wrap:anywhere]">{m.text}</div>
                <div className="text-[10px] text-[var(--rc-muted)] mt-0.5 text-right">{dayjs(m.created_at).format("HH:mm")}</div>
              </div>
              {risky && (
                <div className="max-w-[78%] mt-1 flex items-start gap-1.5 rounded-lg border border-red-500/30 bg-red-500/10 px-2.5 py-1.5 text-[11px] text-red-300" data-testid={`scam-warning-${m.id}`}>
                  <AlertTriangle size={13} className="shrink-0 mt-0.5" /> <span>{t("scam_warning")}</span>
                </div>
              )}
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      {composeRisky && (
        <div className="mb-2 flex items-start gap-1.5 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300" data-testid="scam-compose-warning">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" /> <span>{t("scam_compose_warning")}</span>
        </div>
      )}
      <div className="flex items-center gap-2 pt-2 border-t border-[var(--rc-border)]">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder={t("m_message_seller_ph")} data-testid="market-thread-input"
          className="flex-1 min-w-0 rounded-full bg-[var(--rc-surface)] border border-[var(--rc-border)] px-4 py-2.5 focus:outline-none focus:border-[var(--rc-gold)]/50" />
        <button onClick={send} data-testid="market-thread-send" className="h-11 w-11 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black shrink-0"><Send size={18} /></button>
      </div>

      {reportOpen && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60" onClick={() => setReportOpen(false)} data-testid="market-report-dialog">
          <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl bg-[var(--rc-surface)] border border-[var(--rc-border)] p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-semibold flex items-center gap-2 text-red-400"><Flag size={18} /> {t("m_report_chat")}</div>
              <button onClick={() => setReportOpen(false)} data-testid="market-report-close"><X size={18} /></button>
            </div>
            <p className="text-xs text-[var(--rc-muted)]">{t("m_report_desc").replace("{name}", peer.name)}</p>
            <div className="flex flex-wrap gap-1.5" data-testid="market-report-tags">
              {REASON_TAGS.map((r) => (
                <button key={r.id} onClick={() => setReportTag(reportTag === r.id ? "" : r.id)} data-testid={`report-tag-${r.id}`}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${reportTag === r.id ? "border-red-500 bg-red-500/15 text-red-300" : "border-[var(--rc-border)] text-[var(--rc-muted)]"}`}>
                  {r.label}
                </button>
              ))}
            </div>
            <textarea rows={2} value={reportReason} onChange={(e) => setReportReason(e.target.value)} placeholder={t("m_report_reason_ph")} data-testid="market-report-reason"
              className="w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-[var(--rc-gold)]/50" />
            <button onClick={submitReport} disabled={reporting || (!reportTag && !reportReason.trim())} data-testid="market-report-submit"
              className="w-full rounded-xl bg-red-600 text-white font-semibold py-3 flex items-center justify-center gap-2 disabled:opacity-50"><Flag size={16} /> {t("m_report_submit")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
